import { ReviewOfferProduct } from "../../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { FLEX_MONTHLY, MONTHLY } from "../../../constants/financialProducts";
import {
  POWER_MANAGEMENT_PRODUCT_ID,
  PRODUCT_DISPLAY_ORDER,
  SOLAR_PRODUCT_ID,
} from "../../../constants/bundleAndProductIds";
import { sortProductsByDisplayOrder } from "../../../utils/sortProducts";
import { formatCurrency } from "../../../utils/formatHelpers";

const BACKUP_STORAGE_BATTERY_IDS = new Set<string>(
  PRODUCT_DISPLAY_ORDER.BATTERIES
);

const isMonthlyProductPayment = (
  paymentType: ReviewOfferProduct["paymentType"]
) => paymentType === MONTHLY || paymentType === FLEX_MONTHLY;

const isMonthlyEquipmentLineItem = (product: ReviewOfferProduct) => {
  const cost = Number(product.cost);
  const isZeroCostBattery =
    BACKUP_STORAGE_BATTERY_IDS.has(product.productId) && cost === 0;
  if (
    !isMonthlyProductPayment(product.paymentType) ||
    (cost < 0 && !isZeroCostBattery)
  ) {
    return false;
  }
  return (
    product.productId === SOLAR_PRODUCT_ID ||
    product.productId === POWER_MANAGEMENT_PRODUCT_ID ||
    BACKUP_STORAGE_BATTERY_IDS.has(product.productId)
  );
};

const getYearOneMonthlyEquipmentProducts = (
  products?: ReviewOfferProduct[]
): ReviewOfferProduct[] => {
  if (!products?.length) {
    return [];
  }
  return sortProductsByDisplayOrder(products).filter(
    isMonthlyEquipmentLineItem
  );
};

const getSolarYearOneMonthlyPayment = (
  products?: ReviewOfferProduct[]
): number | undefined => {
  const solar = getYearOneMonthlyEquipmentProducts(products).find(
    (product) => product.productId === SOLAR_PRODUCT_ID
  );
  if (!solar) {
    return undefined;
  }
  return Number(solar.cost);
};

const getBatteryYearOneMonthlyPayment = (
  products?: ReviewOfferProduct[]
): number | undefined => {
  const batteries = getYearOneMonthlyEquipmentProducts(products).filter(
    (product) => BACKUP_STORAGE_BATTERY_IDS.has(product.productId)
  );
  if (batteries.length === 0) {
    return undefined;
  }
  return batteries.reduce((sum, product) => sum + Number(product.cost), 0);
};

type YearOneMonthlyEnergyPaymentDisplay = {
  value: string;
  unit: "/mo";
};

const getYearOneMonthlyEnergyPaymentDisplay = (
  minimumMonthlyPayment: number,
  products?: ReviewOfferProduct[]
): YearOneMonthlyEnergyPaymentDisplay | null => {
  const batteryPayment = getBatteryYearOneMonthlyPayment(products) ?? 0;
  const solar = getYearOneMonthlyEquipmentProducts(products).find(
    (product) => product.productId === SOLAR_PRODUCT_ID
  );

  if (solar) {
    const min =
      solar.minimumCost != null && Number(solar.minimumCost) > 0
        ? Number(solar.minimumCost)
        : Number(solar.cost);
    return {
      value: formatCurrency(min, 2),
      unit: "/mo",
    };
  }

  const minEnergy = minimumMonthlyPayment - batteryPayment;
  if (!Number.isFinite(minEnergy)) {
    return null;
  }

  return {
    value: formatCurrency(minEnergy, 0),
    unit: "/mo",
  };
};

export {
  getBatteryYearOneMonthlyPayment,
  getSolarYearOneMonthlyPayment,
  getYearOneMonthlyEnergyPaymentDisplay,
  getYearOneMonthlyEquipmentProducts,
};
